import { useQuery } from '@apollo/client/react';
import { usePostHog } from '@posthog/react/slim';
import { useCallback } from 'react';

import { JobTitle } from '@pflegia/data-access';
import { RegistrationFormFields } from '@pflegia/types/api/auth/registration';
import { Department } from '@pflegia/types/api/department/department';
import { FunnelQuestion, FunnelQuestionCategory } from '@pflegia/types/api/funnel/questions';
import { LocationObject } from '@pflegia/types/common/location/location-object';
import { locationPropertiesTransformer } from '@pflegia/ui/application/marketing/tracking/utils/location-properties-transformer';
import { transformedEventProperties } from '@pflegia/ui/application/marketing/tracking/utils/transformed-event-properties';
import { useFunnel } from '@pflegia/ui/funnel/hooks/use-funnel';
import { EmploymentTypeAnswer } from '@pflegia/ui/pflegia/funnel/types/employment-type-answer';
import { FunnelAnswers } from '@pflegia/ui/pflegia/funnel/types/funnel-answers';
import { GET_DEPARTMENTS } from '@pflegia/ui/shared/graphql/departments';
import { isBigCityLocation } from '@pflegia/util/common/city-areas/big-cities';

import { QualificationsEntity } from '@funnel/data/candidate.interfaces';

import { FunnelEventMarketing } from '@shared/application/marketing/funnel-event-marketing';
import { getEventProperty } from '@shared/application/marketing/funnel-event-marketing-to-event-property';
import {
  funnelQuestionToFunnelEventMarketing,
  PflegiaFunnelQuestion,
} from '@shared/application/marketing/funnel-question-to-funnel-event-marketing';
import { useTracking } from '@shared/application/tracking.service';

type PflegiaUseFunnel = typeof useFunnel<JobTitle, FunnelAnswers, PflegiaFunnelQuestion>;

export const useTrackAnswer = () => {
  const { trackEventRegistration } = useTracking();
  const posthog = usePostHog();
  const { data: departmentsData } = useQuery<{ departments: Department[] }>(GET_DEPARTMENTS, {
    fetchPolicy: 'cache-first',
  });
  const handleAnswer = useCallback<NonNullable<Parameters<PflegiaUseFunnel>[0]['onAnswer']>>(
    (questionId, category, answer) => {
      if ((questionId as FunnelQuestion) === FunnelQuestion.CityAreas) {
        const areasCount = (answer as LocationObject[] | null)?.length ?? 0;
        trackEventRegistration(
          areasCount > 0 ? FunnelEventMarketing.AreaStepCompleted : FunnelEventMarketing.AreaStepSkipped,
          areasCount > 0 ? transformedEventProperties({ areasCount }) : {},
          questionId,
          category as FunnelQuestionCategory,
        );
        return;
      }

      const eventName = funnelQuestionToFunnelEventMarketing[questionId];
      // Steps without a marketing event (e.g. the non-interactive jobs preview) are not tracked here.
      if (!eventName) return;
      let eventProperties = {};

      switch (questionId) {
        case FunnelQuestion.JobTitle:
          eventProperties = { position: (answer as JobTitle).title };
          break;
        case FunnelQuestion.Locations: {
          const bigCity = (answer as LocationObject[]).some(location => isBigCityLocation(location));
          eventProperties = { ...locationPropertiesTransformer(answer), bigCity };
          // eslint-disable-next-line eslint-compat/camelcase
          posthog?.setPersonProperties(undefined, { big_city_selected: bigCity });
          break;
        }
        case FunnelQuestion.Qualifications:
          eventProperties = { furtherQualificationsValues: (answer as QualificationsEntity[]).map(answerMap => answerMap.title) };
          break;

        case FunnelQuestion.Departments:
          eventProperties = {
            desiredUnitsValues: departmentsData?.departments
              .filter(department => (answer as string[]).includes(department.id))
              .map(department => department.name),
          };
          break;

        case FunnelQuestion.EmploymentType: {
          const { employmentTypes, employmentTypesWeeklyHour } = answer as EmploymentTypeAnswer;
          eventProperties = { employmentScheduleType: employmentTypes?.[0], employmentTypesWeeklyHour };
          break;
        }

        case FunnelQuestion.HighlightJobTitle: {
          eventProperties = Object.hasOwn(answer as JobTitle, 'title')
            ? { position: (answer as JobTitle).title, highlightJobTitle: (answer as JobTitle).title }
            : { highlightJobTitle: answer };
          break;
        }

        case FunnelQuestion.NameAndEmail: {
          const { email, firstName, lastName } = answer as Pick<RegistrationFormFields, 'email' | 'firstName' | 'lastName'>;
          // eslint-disable-next-line eslint-compat/camelcase
          eventProperties = { email, first_name: firstName, last_name: lastName };
          break;
        }

        default:
          eventProperties = { [getEventProperty(eventName)]: answer };
      }

      trackEventRegistration(
        eventName,
        questionId === FunnelQuestion.NameAndEmail ? eventProperties : transformedEventProperties(eventProperties),
        questionId,
        category as FunnelQuestionCategory,
      );
    },
    [departmentsData?.departments, posthog, trackEventRegistration],
  );
  return handleAnswer;
};
